if (!customElements.get('delivery-estimator')) {
  customElements.define(
    'delivery-estimator',
    class DeliveryEstimator extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      optionalSelectionChangeUnsubscriber = undefined;
      _variantRawDate = null;
      _variantAvailability = null;
      _optionalToolDates = new Map();
      _optionalToolAvailabilities = new Map();

      connectedCallback() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.handleVariantChange(event);
        });
        this.optionalSelectionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionalSelectionChange, (event) => {
          this.handleOptionalToolChange(event);
        });
        this.initializeFromCurrentVariant();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = undefined;
        }
        if (this.optionalSelectionChangeUnsubscriber) {
          this.optionalSelectionChangeUnsubscriber();
          this.optionalSelectionChangeUnsubscriber = undefined;
        }
      }

      handleVariantChange(event) {
        const { variant } = event.data;

        if (!variant) {
          this._variantRawDate = null;
          this.recalculateEffectiveDelivery();
          return;
        }

        const variantId = String(variant.id);
        const nextDeliveryDateRaw = this.getVariantNextDeliveryFromDom(variantId);

        if (nextDeliveryDateRaw) {
          this._variantRawDate = nextDeliveryDateRaw;
        } else {
          // Fallback: compute start date from days offset as yyyy-mm-dd inline
          const estimatedDays = parseInt(this.dataset.connectedMetafield || 0, 10) || 0;
          const start = new Date();
          start.setDate(start.getDate() + estimatedDays);
          const y = start.getFullYear();
          const m = String(start.getMonth() + 1).padStart(2, '0');
          const d = String(start.getDate()).padStart(2, '0');
          this._variantRawDate = `${y}-${m}-${d}`;
        }

        this._variantAvailability = this.getVariantAvailabilityFromDom(variantId);
        this.recalculateEffectiveDelivery();
      }

      handleOptionalToolChange(event) {
        const source = event.source;
        const optionalDelivery = source?.dataset?.variantDelivery;

        const isRadio = source?.type === 'radio';
        const mapKey = isRadio && source?.name ? source.name : source;

        const optionalAvailability = source?.dataset?.variantAvailability;

        if (optionalDelivery) {
          this._optionalToolDates.set(mapKey, optionalDelivery);
          if (optionalAvailability) {
            this._optionalToolAvailabilities.set(mapKey, optionalAvailability);
          } else {
            this._optionalToolAvailabilities.delete(mapKey);
          }
        } else {
          this._optionalToolDates.delete(mapKey);
          this._optionalToolAvailabilities.delete(mapKey);
        }

        this.recalculateEffectiveDelivery();
      }

      initializeFromCurrentVariant() {
        const variantId = this.getCurrentVariantIdFromForm();

        if (!variantId) {
          this._variantRawDate = null;
          this.recalculateEffectiveDelivery();
          return;
        }

        const nextDeliveryDateRaw = this.getVariantNextDeliveryFromDom(String(variantId));

        if (nextDeliveryDateRaw) {
          this._variantRawDate = nextDeliveryDateRaw;
        } else {
          const estimatedDays = parseInt(this.dataset.connectedMetafield || 0, 10) || 0;
          const start = new Date();
          start.setDate(start.getDate() + estimatedDays);
          const y = start.getFullYear();
          const m = String(start.getMonth() + 1).padStart(2, '0');
          const d = String(start.getDate()).padStart(2, '0');
          this._variantRawDate = `${y}-${m}-${d}`;
        }

        this._variantAvailability = this.getVariantAvailabilityFromDom(String(variantId));
        this.recalculateEffectiveDelivery();
      }

      recalculateEffectiveDelivery() {
        const deliveryElement = this.querySelector('.delivery');
        const deliveryDateSpan = deliveryElement?.querySelector('.delivery_date');
        const availabilityEl = deliveryElement?.querySelector('.delivery-availability');

        // Gather all active dates: variant + every selected optional tool
        const allDates = [this._variantRawDate, ...this._optionalToolDates.values()].filter(Boolean);

        if (!allDates.length) {
          this.clearDeliveryProperty();
          return;
        }

        if (!deliveryDateSpan) return;

        // yyyy-mm-dd compares correctly as plain strings — no Date parsing needed
        const maxDate = allDates.reduce((max, d) => (d > max ? d : max));
        const dateRange = this.calculateDeliveryRangeFromFixedDate(maxDate, 3);

        if (dateRange) {
          deliveryDateSpan.textContent = dateRange;
          deliveryDateSpan.dataset.rawDeliveryDate = maxDate;
          this.upsertDeliveryProperty(dateRange);
        } else {
          this.clearDeliveryProperty();
        }

        if (availabilityEl) {
          const allAvailabilities = [
            this._variantAvailability,
            ...this._optionalToolAvailabilities.values()
          ].filter(v => v !== null && v !== undefined && v !== '');

          const minQty = allAvailabilities.length
            ? allAvailabilities.reduce((min, v) => (parseInt(v, 10) < parseInt(min, 10) ? v : min))
            : null;

          if (minQty) {
            availabilityEl.innerHTML = `<span class="delivery-live-dot"></span>Only ${minQty} left`;
            availabilityEl.classList.remove('hidden');
          } else {
            availabilityEl.classList.add('hidden');
          }
        }

      }

      getCurrentVariantIdFromForm() {
        const form = this.getProductFormElement();
        const input = form?.querySelector('[name="id"]');
        const value = input?.value;

        const id = Number(value);
        return Number.isFinite(id) && id > 0 ? String(id) : null;
      }

      get deliveryPropertyEnabled() {
        // Set data-disable-property on the element to turn off property injection.
        return !('disableProperty' in this.dataset);
      }

      get deliveryPropertyName() {
        // This becomes the line item property label in cart/order.
        return this.dataset.deliveryPropertyName || 'Delivery date';
      }

      get deliveryPropertyInputName() {
        return `properties[${this.deliveryPropertyName}]`;
      }

      getProductFormElement() {
        const root = this.closest('product-info') || document;

        // Prefer the closest product section.
        const form = root.querySelector('product-form form');
        if (form) return form;

        // Fallback for other templates/quick add.
        return document.querySelector('product-form form');
      }

      upsertDeliveryProperty(value) {
        if (!this.deliveryPropertyEnabled) return;
        const form = this.getProductFormElement();
        if (!form) return;

        const safeValue = typeof value === 'string' ? value.trim() : '';
        if (!safeValue) {
          this.clearDeliveryProperty();
          return;
        }

        const inputName = this.deliveryPropertyInputName;
        let input = form.querySelector(`input[name="${CSS.escape(inputName)}"]`);

        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = inputName;

          // Keep consistency with existing code that inserts hidden inputs before product-id when present.
          const productIdElement = form.querySelector('[name="product-id"]');
          if (productIdElement) {
            form.insertBefore(input, productIdElement);
          } else {
            form.appendChild(input);
          }
        }

        input.value = safeValue;
      }

      clearDeliveryProperty() {
        if (!this.deliveryPropertyEnabled) return;
        const form = this.getProductFormElement();
        if (!form) return;

        const inputName = this.deliveryPropertyInputName;
        const input = form.querySelector(`input[name="${CSS.escape(inputName)}"]`);
        if (input) input.value = '';
      }

      getVariantNextDeliveryFromDom(variantId) {
        const root = this.closest('product-info') || document;
        const input = root.querySelector(
          `.variant-next-delivery-date input[value="${CSS.escape(String(variantId))}"]`
        );
        if (!input) return '';

        // Detect market via Shopify global (e.g. 'GB', 'DE', 'FR')
        const market = (Shopify?.country || '').toUpperCase();

        let priority, standard;

        if (market === 'DE') {
          priority = input.dataset.priorityDeliveryDateDe;
          standard = input.dataset.standardDeliveryDateDe;
        } else if (market === 'FR') {
          priority = input.dataset.priorityDeliveryDateFr;
          standard = input.dataset.standardDeliveryDateFr;
        } else {
          // Default: GB / UK
          priority = input.dataset.priorityDeliveryDate;
          standard = input.dataset.standardDeliveryDate;
        }

        // Return the earliest valid date (yyyy-mm-dd sorts correctly as plain strings)
        const candidates = [priority, standard].filter(
          (d) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d.trim())
        );
        if (!candidates.length) return '';

        return candidates.reduce((min, d) => (d < min ? d : min));
      }

      getVariantAvailabilityFromDom(variantId) {
        const root = this.closest('product-info') || document;
        const input = root.querySelector(
          `.variant-next-delivery-date input[value="${CSS.escape(String(variantId))}"]`
        );
        return input?.dataset.nextDeliveryAvailability || null;
      }

      calculateDeliveryRangeFromFixedDate(yyyyMmDd, bufferDays) {
        const start = this.parseYyyyMmDdToLocalDate(yyyyMmDd);
        if (!start) return null;

        const end = new Date(start);
        end.setDate(end.getDate() + (parseInt(bufferDays, 10) || 0));

        return this.formatDateRange(start, end);
      }

      parseYyyyMmDdToLocalDate(yyyyMmDd) {
        if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;

        // Expected format: 2026-03-19
        const match = yyyyMmDd.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;

        const year = Number(match[1]);
        const monthIndex = Number(match[2]) - 1; // 0-based
        const day = Number(match[3]);

        const date = new Date(year, monthIndex, day);
        if (Number.isNaN(date.getTime())) return null;

        return date;
      }

      formatDateRange(dateStart, dateEnd) {
        const locale = document.documentElement.lang || 'en-US';
        const dayStart = dateStart.getDate();
        const monthStart = dateStart.toLocaleString(locale, { month: 'short' });
        const dayEnd = dateEnd.getDate();
        const monthEnd = dateEnd.toLocaleString(locale, { month: 'short' });

        if (monthStart === monthEnd) {
          return `${dayStart} - ${dayEnd} ${monthStart}`;
        }

        return `${dayStart} ${monthStart} - ${dayEnd} ${monthEnd}`;
      }

      calculateDeliveryDates(estimatedDays, estimatedDaysEnd) {
        const today = Math.floor(Date.now() / 1000); // Unix timestamp in seconds

        const deliverySecondsStart = estimatedDays * 86400;
        const deliverySecondsEnd = (estimatedDays + estimatedDaysEnd) * 86400;
        const deliveryTimestampStart = today + deliverySecondsStart;
        const deliveryTimestampEnd = today + deliverySecondsEnd;

        const dateStart = new Date(deliveryTimestampStart * 1000);
        const dateEnd = new Date(deliveryTimestampEnd * 1000);

        return this.formatDateRange(dateStart, dateEnd);
      }
    }
  );
}
